import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RQAPICall } from 'store/APIClient';
import {
    BudgetProposalSettingsT,
    BudgetProposalT,
} from 'routes/BudgetModule/config/types';
import { useSettingsQuery } from 'routes/BudgetModule/components/BudgetProposal/Resource/hooks/settings';
import { transformUrl } from 'helpers/urlHelpers';
import { snakify } from 'helpers/syntaxHelper';

export const useGetBudgetProposalSettings = (
    budgetProposal: BudgetProposalT,
    resourceType: string,
    softwareId: string,
): {
    settings: BudgetProposalSettingsT | undefined;
    isSettingsLoading: boolean;
    isError: boolean;
} => {
    const queryKey = [
        'budget_proposal_settings',
        budgetProposal.id.toString(),
        softwareId,
    ];

    const {
        data: settings,
        isLoading: isSettingsLoading,
        isError,
    } = useSettingsQuery({
        budgetProposalId: budgetProposal.id,
        resourceType,
        softwareId,
        cacheKey: queryKey,
        options: {
            enabled: !!softwareId,
        },
    });
    return { settings, isSettingsLoading, isError };
};

export function useMutateBudgetProposalSettings(
    budgetProposal: BudgetProposalT,
    resourceType: string,
    softwareId: string | null,
) {
    const queryClient = useQueryClient();
    const queryKey = [
        'budget_proposal_settings',
        budgetProposal.id.toString(),
        softwareId,
    ];

    return useMutation<
        { content: { data: BudgetProposalSettingsT } },
        unknown,
        Partial<BudgetProposalSettingsT>
    >({
        mutationFn: (body) =>
            RQAPICall({
                endpoint: `budget/proposals/${budgetProposal.id}/${resourceType}/${softwareId}/settings`,
                method: 'PATCH',
                body,
                preProcessingUrlCallback: (url) =>
                    transformUrl(url, [3], snakify),
            }),
        onSuccess: (data) => {
            queryClient.setQueryData<BudgetProposalSettingsT>(
                queryKey,
                (oldData) => ({
                    ...oldData,
                    ...data.content.data,
                }),
            );
        },
    });
}
